import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import type { MonospaceFile } from '#layers/data-model/public';
import { computed, toValue } from 'vue';
import { useAppConfig } from '#app/config';
import { useRuntimeConfig } from '#app/nuxt';
import { getAssetUrl } from '#layers/assets/utils/asset-url';

export const AssetUrlScopes = {
	System: 'system',
	Workspace: 'workspace',
} as const;

export type AssetUrlScope = typeof AssetUrlScopes[keyof typeof AssetUrlScopes];

export interface UseAssetUrlOptions {
	scope: MaybeRefOrGetter<AssetUrlScope>;
}

type AssetUrlInput = MonospaceFile | string | null;
type AssetUrlResult<T extends AssetUrlInput> = ComputedRef<T extends null ? string | null : string>;

export function useAssetUrl<T extends AssetUrlInput>(fileOrId: MaybeRefOrGetter<T>, options: UseAssetUrlOptions): AssetUrlResult<T> {
	const appConfig = useAppConfig();
	const runtimeConfig = useRuntimeConfig();

	return computed(() => {
		const fileOrIdValue = toValue(fileOrId);
		const scope = toValue(options.scope);

		return fileOrIdValue
			? getAssetUrl(fileOrIdValue, {
					apiBase: runtimeConfig.public.api,
					workspaceSlug: scope === AssetUrlScopes.Workspace ? appConfig.monospace.workspaceSlug : undefined,
				})
			: null;
	}) as AssetUrlResult<T>;
}

export function useWorkspaceAssetUrl<T extends AssetUrlInput>(fileOrId: MaybeRefOrGetter<T>) {
	return useAssetUrl(fileOrId, { scope: AssetUrlScopes.Workspace });
}

export function useSystemAssetUrl<T extends AssetUrlInput>(fileOrId: MaybeRefOrGetter<T>) {
	return useAssetUrl(fileOrId, { scope: AssetUrlScopes.System });
}
